import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import { getPollMs } from '../utils/appSettings';
import i18n from '../i18n';
import { useLayoutImage } from '../context/LayoutContext';
import { useIsMobile } from '../Responsive';
import { QRCodeSVG } from 'qrcode.react';
import { bbqTerm, bbqIcon } from '../utils/bbqTerms';

const tr = i18n.t.bind(i18n);
const API = (p) => `${config.API_BASE_URL}${p}`;
const bit = (v) => (v && v.data ? v.data[0] : Number(v) || 0);
const eur = (c) => (Number(c || 0) / 100).toFixed(2) + '€';
const FALLBACK_COURSES = { 1: tr('Starters'), 2: tr('Mains'), 4: tr('Sides'), 5: tr('Drinks'), 3: tr('Desserts') };
const serverErr = (e, fallback) => tr(e?.response?.data?.error || fallback);

const lineTotal = (it, priceCents, perKg) =>
  perKg ? Math.round(it.qty * priceCents / 1000) : it.qty * priceCents;

const chipStyle = (active) => ({
  padding: '9px 14px', borderRadius: 18, border: '1px solid #ccc', cursor: 'pointer',
  whiteSpace: 'nowrap', fontSize: 14, background: active ? '#198754' : 'white',
  color: active ? 'white' : '#222', borderColor: active ? '#198754' : '#ccc',
});

const OrdersPage = () => {
  const layoutImage = useLayoutImage();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [map, setMap] = useState({ tables: [], openOrders: [], sittingTables: [] });
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [cooktypes, setCooktypes] = useState([]);
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [tab, setTab] = useState('menu');          // 'menu' | 'bill'
  const [selCat, setSelCat] = useState(null);
  const [course, setCourse] = useState(null); // αρχικοποιείται στο ΠΡΩΤΟ course μόλις φορτώσει το μενού
  const courseName = (id) => (menu.courses || []).find((c) => c.id === Number(id))?.name
    || FALLBACK_COURSES[id] || '';
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [discount, setDiscount] = useState('');
  const [guestUrl, setGuestUrl] = useState('');
  const [qrModal, setQrModal] = useState(null);   // { title, url }
  const [expandedItems, setExpandedItems] = useState({}); // menu_item -> inline detail open
  const toggleExpand = (id) => setExpandedItems((p) => ({ ...p, [id]: !p[id] }));
  const [selArea, setSelArea] = useState(null);

  // ---- floor plan (same design space as Service): auto-zoom στον ενεργό χώρο ----
  const DESIGN_W = 800; const DESIGN_H = 800;
  const resizerRef = React.useRef(null);
  const mapScrollRef = React.useRef(null);
  const [containerW, setContainerW] = useState(0);
  const [zoom, setZoom] = useState(1); // χειροκίνητος πολλαπλασιαστής πάνω στο auto-fit
  useEffect(() => {
    const node = resizerRef.current;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      setContainerW(entries[0]?.contentRect?.width || node.clientWidth || DESIGN_W);
    });
    ro.observe(node);
    return () => ro.disconnect();
  });

  const loadMap = useCallback(async () => {
    try {
      const [{ data: m }, { data: mn }, { data: ct }] = await Promise.all([
        axios.get(API('/ordering/table-map')),
        axios.get(API('/ordering/menu')),
        axios.get(API('/cooktypes')),
      ]);
      setMap(m); setMenu(mn); setCooktypes(Array.isArray(ct) ? ct : []);
      // Προεπιλογή: το ΠΡΩΤΟ course (κατά sort) και η πρώτη ενεργή κατηγορία του
      const firstCourseId = (mn.courses || [])[0]?.id ?? 1;
      setCourse((c) => c ?? firstCourseId);
      setSelCat((c) => c || mn.categories.filter((x) => bit(x.active) && Number(x.default_course || 2) === firstCourseId)[0]?.id
        || mn.categories.filter((x) => bit(x.active))[0]?.id || null);
    } catch (e) { /* polling retries */ }
  }, []);
  useEffect(() => { loadMap(); const t = setInterval(loadMap, Math.max(getPollMs('normal'), 5000)); return () => clearInterval(t); }, [loadMap]);

  const loadOrder = useCallback(async (id) => {
    try { const { data } = await axios.get(API(`/ordering/orders/${id}`)); setOrder(data); }
    catch (e) { toast.error(tr('Failed to load.')); }
  }, []);
  useEffect(() => { if (orderId) loadOrder(orderId); }, [orderId, loadOrder]);

  // Κάθε φορά που ανοίγει παραγγελία, ξεκίνα από το ΠΡΩΤΟ tab (π.χ. Πρώτα) — όχι τα Κυρίως
  useEffect(() => {
    if (!orderId) return;
    const fc = (menu.courses || [])[0]?.id;
    if (!fc) return;
    setCourse(fc);
    setSelCat(menu.categories.filter((x) => bit(x.active) && Number(x.default_course || 2) === fc)[0]?.id
      || menu.categories.filter((x) => bit(x.active))[0]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // deep link: /orders?open=<orderId>
  useEffect(() => {
    const openId = Number(searchParams.get('open'));
    if (openId) { setOrderId(openId); setSearchParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openByTable = useMemo(() => {
    const m = new Map();
    map.openOrders.forEach((o) => m.set(o.table_id, o));
    return m;
  }, [map.openOrders]);

  // one chip per open order (dedup: an order may span tables)
  const openOrderChips = useMemo(() => {
    const seen = new Map();
    map.openOrders.forEach((o) => {
      const t = map.tables.find((x) => x.id === o.table_id);
      if (!seen.has(o.order_id)) seen.set(o.order_id, { ...o, numbers: [] });
      if (t) seen.get(o.order_id).numbers.push(t.number);
    });
    return [...seen.values()];
  }, [map.openOrders, map.tables]);

  const areas = useMemo(() => {
    const by = new Map();
    map.tables.forEach((t) => {
      const k = t.area_name || '—';
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(t);
    });
    return [...by.entries()];
  }, [map.tables]);

  // Auto-zoom: bounding box των τραπεζιών του ενεργού tab → κλίμακα ώστε να
  // χωράνε ΟΛΑ τα τραπέζια (και σε πλάτος ΚΑΙ σε ύψος), και scroll εκεί.
  // Το zoom είναι επιπλέον χειροκίνητος πολλαπλασιαστής.
  const ALL_AREAS = '__all__'; // ψευδο-tab «Όλη η κάτοψη» — πρώτο όταν οι χώροι > 1
  const mapView = useMemo(() => {
    const areaNames = areas.map(([a]) => a);
    const hasAllTab = areaNames.length > 1;
    const activeArea = selArea || (hasAllTab ? ALL_AREAS : areaNames[0]);
    const tbls = activeArea === ALL_AREAS
      ? map.tables
      : (areas.find(([a]) => a === activeArea) || [null, []])[1];
    const PAD = 30;
    let x0 = 0, y0 = 0, x1 = DESIGN_W, y1 = DESIGN_H;
    if (tbls.length) {
      x0 = Math.max(0, Math.min(...tbls.map((t) => Number(t.monitor_x || 0))) - PAD);
      y0 = Math.max(0, Math.min(...tbls.map((t) => Number(t.monitor_y || 0))) - PAD);
      x1 = Math.min(DESIGN_W, Math.max(...tbls.map((t) => Number(t.monitor_x || 0) + Number(t.width || 46))) + PAD);
      y1 = Math.min(DESIGN_H, Math.max(...tbls.map((t) => Number(t.monitor_y || 0) + Number(t.height || 40))) + PAD);
    }
    const bw = Math.max(120, x1 - x0);
    const bh = Math.max(120, y1 - y0);
    const cw = containerW || 600;
    const MAX_H = 620;
    // Fit και στις δύο διαστάσεις ώστε το auto-zoom να δείχνει ΟΛΑ τα τραπέζια του tab
    const fit = Math.min(cw / bw, MAX_H / bh);
    const f = Math.min(3, Math.max(0.25, fit)) * zoom;
    return {
      areaNames, hasAllTab, activeArea, tbls, f,
      viewH: Math.min(Math.round(bh * f) + 2, MAX_H),
      sx: Math.round(x0 * f), sy: Math.round(y0 * f),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas, map.tables, selArea, containerW, zoom]);

  useEffect(() => {
    mapScrollRef.current?.scrollTo({ left: mapView.sx, top: mapView.sy, behavior: 'smooth' });
  }, [mapView.sx, mapView.sy]);

  const backToMap = () => {
    setOrderId(null); setOrder(null); setCart([]); setTab('menu');
    setGuestUrl(''); setMoreOpen(false); loadMap();
  };

  const tapTable = async (t) => {
    const open = openByTable.get(t.id);
    if (open) { setOrderId(open.order_id); return; }
    try {
      const { data } = await axios.post(API('/ordering/orders'), { tableIds: [t.id] });
      if (data.createdSitting) toast.info(tr('Walk-in sitting created for this table.'), { autoClose: 2500 });
      setOrderId(data.id);
    } catch (e) { toast.error(serverErr(e, 'Failed to open order.')); }
  };

  const addToCart = (mi) => {
    if (bit(mi.out_of_stock)) { toast.info(tr('Out of stock')); return; }
    const perKg = bit(mi.per_kg);
    if (perKg || bit(mi.is_meat)) setExpandedItems((p) => ({ ...p, [mi.id]: true }));
    setCart((p) => {
      const ix = p.findIndex((l) => l.menu_item === mi.id && l.course === course && !l.note);
      if (ix >= 0 && !perKg) {
        const n = [...p]; n[ix] = { ...n[ix], qty: n[ix].qty + 1 }; return n;
      }
      const md = bit(mi.is_meat) && mi.meat ? meatDefaults.get(mi.meat) : null;
      return [...p, {
        menu_item: mi.id, name: mi.name,
        qty: perKg ? (Number(md?.default_quantity) >= 100 ? Number(md.default_quantity) : 300) : 1,
        per_kg: perKg, price_cents: mi.price_cents, course, note: '',
        is_meat: bit(mi.is_meat), cooktype: md?.default_cooktype ? String(md.default_cooktype) : '',
        standalone: false, filleting: false, pairWith: '', // τίποτα προεπιλεγμένο — τα διαλέγει ο σερβιτόρος
      }];
    });
  };

  const sendCart = async () => {
    if (!cart.length || busy) return;
    if (cart.some((l) => l.is_meat && !l.cooktype)) {
      setCartOpen(true);
      toast.warning(bbqTerm('Select a cook type for every meat.'));
      return;
    }
    setBusy(true);
    try {
      await axios.post(API(`/ordering/orders/${orderId}/items`), {
        items: cart.map((l, ix) => {
          let pairName = '', pair_ref, pair_assignedmeat;
          if (l.pairWith?.startsWith('cart:')) {
            pair_ref = Number(l.pairWith.slice(5));
            pairName = cart[pair_ref]?.name || '';
          } else if (l.pairWith?.startsWith('am:')) {
            pair_assignedmeat = Number(l.pairWith.slice(3));
            pairName = (order?.items || []).find((x) => x.assignedmeat === pair_assignedmeat)?.item_name || '';
          }
          return {
            menu_item: l.menu_item, qty: l.qty, course: l.course, ref: ix,
            note: [pairName ? `${tr('with')} ${pairName}` : '', l.note].filter(Boolean).join(' · ') || null,
            cooktype: l.cooktype || null,
            standalone: l.standalone ? 1 : 0, filleting: l.filleting ? 1 : 0,
            pair_ref, pair_assignedmeat,
          };
        }),
      });
      setCart([]); setCartOpen(false);
      toast.success(tr('Order sent to stations.'), { autoClose: 2000 });
      loadOrder(orderId); loadMap();
    } catch (e) { toast.error(serverErr(e, 'Failed to send.')); }
    finally { setBusy(false); }
  };

  const totals = order?.totals;
  const balance = totals ? totals.balance : 0;

  const addDiscount = async (kind, value, order_item = null) => {
    try {
      await axios.post(API(`/ordering/orders/${orderId}/discounts`), { kind, value, order_item });
      setDiscount(''); loadOrder(orderId);
    } catch (e) { toast.error(serverErr(e, 'Save failed.')); }
  };

  const payCash = async () => {
    const amt = Math.round(parseFloat(String(cashAmount || (balance / 100)).replace(',', '.')) * 100);
    if (!amt || amt <= 0) return;
    try {
      await axios.post(API(`/ordering/orders/${orderId}/payments`), { method: 'cash', amount_cents: amt });
      setCashAmount(''); loadOrder(orderId);
      toast.success(tr('Payment recorded.'), { autoClose: 2000 });
    } catch (e) { toast.error(serverErr(e, 'Save failed.')); }
  };

  const payTerminal = async () => {
    try {
      await axios.post(API(`/ordering/orders/${orderId}/payments`), { method: 'card_terminal', amount_cents: balance });
      toast.info(tr('Sent to the card terminal — complete the payment on the device.'));
      loadOrder(orderId);
    } catch (e) { toast.error(serverErr(e, 'Card terminal is not configured yet.')); }
  };

  const issueReceipt = async () => {
    try {
      const { data } = await axios.post(API(`/ordering/orders/${orderId}/receipt`));
      toast.success(data.status === 'issued' ? tr('Receipt issued.') : tr('Receipt queued (provider pending).'), { autoClose: 3000 });
    } catch (e) { toast.error(serverErr(e, 'Save failed.')); }
  };

  const mintGuestLink = async () => {
    try {
      const { data } = await axios.post(API(`/ordering/orders/${orderId}/guest-link`));
      setGuestUrl(data.url);
      setQrModal({ title: tr('Guest ordering QR'), url: data.url });
      try { await navigator.clipboard.writeText(data.url); } catch {}
    } catch (e) { toast.error(serverErr(e, 'Save failed.')); }
  };

  const approveDrafts = async () => {
    try {
      const { data } = await axios.post(API(`/ordering/orders/${orderId}/approve-drafts`));
      toast.success(tr('Guest items approved: {{n}}', { n: data.approved }));
      loadOrder(orderId);
    } catch (e) { toast.error(tr('Save failed.')); }
  };

  const voidItem = async (i) => {
    if (!window.confirm(tr('Void "{{name}}";', { name: i.item_name }))) return;
    try {
      await axios.put(API(`/ordering/order-items/${i.id}/status`), { status: 'void' });
      loadOrder(orderId);
    } catch (e) { toast.error(tr('Save failed.')); }
  };

  const voidOrder = async () => {
    if (!window.confirm(tr('Void the ENTIRE order?'))) return;
    try { await axios.post(API(`/ordering/orders/${orderId}/void`)); backToMap(); }
    catch (e) { toast.error(serverErr(e, 'Save failed.')); }
  };

  const closeOrder = async () => {
    const msg = balance > 0
      ? tr('Unpaid balance {{eur}} — close anyway?', { eur: eur(balance) })
      : tr('Close this order?');
    if (!window.confirm(msg)) return;
    try { await axios.post(API(`/ordering/orders/${orderId}/close`)); backToMap(); }
    catch (e) { toast.error(serverErr(e, 'Failed to close.')); }
  };

  const renderCartLine = (l, ix, showName = true) => {
    const setL = (patch) => setCart((p) => p.map((x, i) => (i === ix ? { ...x, ...patch } : x)));
    const step = l.per_kg ? 50 : 1;
    const min = l.per_kg ? 50 : 1;
    const stepBtn = { width: 36, height: 36, border: 'none', background: '#f2f3f2', fontSize: 19, cursor: 'pointer', lineHeight: 1 };
    const chip = (active, label, onClick) => (
      <button onClick={onClick} style={{
        padding: '6px 12px', borderRadius: 16, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1.5px solid ${active ? '#333' : '#d5d5d5'}`,
        background: active ? '#333' : 'white', color: active ? 'white' : '#555',
      }}>{label}</button>
    );
    return (
      <div key={ix} style={{
        background: 'white', border: '1px solid #e4e4e4', borderRadius: 12,
        padding: '9px 10px', marginTop: 7, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        {showName && (
          <div style={{ display: 'flex', fontSize: 13, marginBottom: 7 }}>
            <b style={{ flex: 1 }}>{l.name}</b>
            <span style={{ opacity: 0.45, fontSize: 11 }}>{courseName(l.course)}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 20, overflow: 'hidden' }}>
            <button style={stepBtn} onClick={() => setL({ qty: Math.max(min, (Number(l.qty) || 0) - step) })}>−</button>
            <input value={l.qty} inputMode="numeric"
              onChange={(e) => setL({ qty: Number(String(e.target.value).replace(/[^0-9]/g, '')) || 0 })}
              style={{ width: l.per_kg ? 54 : 36, border: 'none', textAlign: 'center', fontWeight: 700, fontSize: 15, outline: 'none' }} />
            {l.per_kg ? <span style={{ fontSize: 11, opacity: 0.5, paddingRight: 6 }}>{tr('g')}</span> : null}
            <button style={stepBtn} onClick={() => setL({ qty: (Number(l.qty) || 0) + step })}>+</button>
          </span>

          {!!l.is_meat && (
            <select value={l.cooktype} onChange={(e) => setL({ cooktype: e.target.value })}
              style={{
                flex: 1, minWidth: 104, padding: '9px 6px', borderRadius: 20, fontSize: 13.5,
                border: `1.5px solid ${l.cooktype ? '#d5d5d5' : '#dc3545'}`,
                background: l.cooktype ? 'white' : 'rgba(220,53,69,0.05)',
              }}>
              <option value="">🔥 {tr('Cook type')}</option>
              {cooktypes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {!l.is_meat && (
            <select value={l.pairWith} onChange={(e) => setL({ pairWith: e.target.value })}
              style={{ flex: 1, minWidth: 110, padding: '9px 6px', borderRadius: 20, fontSize: 12.5, border: '1.5px solid #d5d5d5' }}>
              <option value="">{tr('With all mains')}</option>
              {cart.map((x, i) => (x.is_meat ? <option key={`c${i}`} value={`cart:${i}`}>{x.name}</option> : null))}
              {(order?.items || []).filter((x) => x.status !== 'void' && x.assignedmeat)
                .map((x) => <option key={`a${x.assignedmeat}`} value={`am:${x.assignedmeat}`}>{x.item_name}</option>)}
            </select>
          )}

          <b style={{ marginLeft: 'auto', fontSize: 14.5, whiteSpace: 'nowrap' }}>
            {eur(lineTotal(l, l.price_cents, l.per_kg))}
          </b>
          <button onClick={() => setCart((p) => p.filter((_, i) => i !== ix))}
            style={{ border: 'none', background: 'none', color: '#b02a37', fontSize: 17, cursor: 'pointer', padding: 2 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
          {!!l.is_meat && chip(!!l.standalone, tr('Alone'), () => setL({ standalone: !l.standalone }))}
          {!!l.is_meat && chip(!!l.filleting, tr('Fillet'), () => setL({ filleting: !l.filleting }))}
          <input placeholder={'📝 ' + tr('Note')} value={l.note}
            onChange={(e) => setL({ note: e.target.value })}
            style={{ flex: 1, minWidth: 80, padding: '7px 12px', borderRadius: 16, border: '1px solid #e0e0e0', fontSize: 12.5 }} />
        </div>
      </div>
    );
  };

  const cartTotal = cart.reduce((s, l) => s + lineTotal(l, l.price_cents, l.per_kg), 0);
  const cartCount = cart.reduce((s, l) => s + (l.per_kg ? 1 : Number(l.qty) || 0), 0);

  // Already-ordered context per product: sent in THIS order, in the cart now,
  // and the reservation's meat PRE-ORDER (AssignMeats) mapped via menu_items.meat.
  const sentByItem = useMemo(() => {
    const m = new Map();
    (order?.items || []).filter((i) => i.status !== 'void')
      .forEach((i) => m.set(i.menu_item, (m.get(i.menu_item) || 0) + Number(i.qty)));
    return m;
  }, [order]);
  const cartByItem = useMemo(() => {
    const m = new Map();
    cart.forEach((l) => m.set(l.menu_item, (m.get(l.menu_item) || 0) + Number(l.qty)));
    return m;
  }, [cart]);
  const preByMeat = useMemo(() => {
    const m = new Map();
    (order?.preordered || []).forEach((p) => {
      const cur = m.get(p.meat) || { grams: 0, count: 0 };
      m.set(p.meat, { grams: cur.grams + Number(p.grams || 0), count: cur.count + 1 });
    });
    return m;
  }, [order]);
  const meatDefaults = useMemo(() => {
    const m = new Map();
    (menu.meatDefaults || []).forEach((x) => m.set(x.id, x));
    return m;
  }, [menu]);
  const sentItems = (order?.items || []).filter((i) => i.status !== 'void');
  const draftCount = (order?.items || []).filter((i) => i.status === 'draft').length;

  // ============================================================
  // HOME: open orders + floor plan
  // ============================================================
  if (!orderId) {
    const { areaNames, hasAllTab, activeArea, tbls, f, viewH } = mapView;
    const sittingSet = new Set(map.sittingTables || []);
    return (
      <div style={{ maxWidth: 860, margin: '10px auto', padding: '0 10px 30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: '6px 0' }}>{tr('Orders')}</h2>
        </div>

        {openOrderChips.length > 0 && (
          <>
            <div style={{ fontSize: 13, opacity: 0.65, margin: '4px 0 6px' }}>
              {tr('Open orders')} ({openOrderChips.length})
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
              {openOrderChips.map((o) => (
                <button key={o.order_id} onClick={() => setOrderId(o.order_id)}
                  style={{
                    border: '2px solid #198754', background: 'rgba(25,135,84,0.08)',
                    borderRadius: 12, padding: '8px 14px', cursor: 'pointer',
                    whiteSpace: 'nowrap', textAlign: 'left',
                  }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {tr('Table')} {o.numbers.join('+') || '—'}
                  </div>
                  <div style={{ fontSize: 12 }}>{o.items_count}× · {eur(o.total_cents)}</div>
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ fontSize: 13, opacity: 0.65, margin: '10px 0 6px' }}>
          {tr('Tap a table to start or continue an order')}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, overflowX: 'auto', alignItems: 'center' }}>
          {hasAllTab && (
            <button style={chipStyle(activeArea === '__all__')}
              onClick={() => { setSelArea('__all__'); setZoom(1); }}>{tr('Layout')}</button>
          )}
          {areaNames.map((a) => (
            <button key={a} style={chipStyle(activeArea === a)}
              onClick={() => { setSelArea(a); setZoom(1); }}>{a}</button>
          ))}
          {map.restricted && (
            <span style={{
              fontSize: 12, color: '#92400e', background: '#fef3c7', borderRadius: 10,
              padding: '4px 10px', whiteSpace: 'nowrap',
            }}>📅 {tr('Your areas (schedule)')}</span>
          )}
        </div>

        <div ref={resizerRef} style={{ position: 'relative' }}>
          <div ref={mapScrollRef}
            style={{ width: '100%', height: viewH, overflow: 'auto', border: '1px solid #ddd', borderRadius: 12 }}>
            {/* Πλήρης κάτοψη σε κλίμακα auto-fit×zoom — το scroll εστιάζει στον ενεργό χώρο */}
            <div style={{
              position: 'relative', width: DESIGN_W * f, height: DESIGN_H * f,
              backgroundImage: layoutImage ? `url(${layoutImage})` : 'none',
              backgroundSize: `${DESIGN_W * f}px ${DESIGN_H * f}px`,
              backgroundRepeat: 'no-repeat', backgroundColor: '#fbfbfb',
            }}>
              {tbls.map((t) => {
                const open = openByTable.get(t.id);
                const seated = sittingSet.has(t.id); // ενεργό sitting σήμερα
                // Μικρότερα ελάχιστα στο «Όλη η κάτοψη» ώστε να μην αλληλοκαλύπτονται σε μικρή κλίμακα
                const w = Math.max(activeArea === '__all__' ? 26 : 40, Number(t.width || 46) * f);
                const h = Math.max(activeArea === '__all__' ? 22 : 34, Number(t.height || 40) * f);
                return (
                  <button key={t.id} onClick={() => tapTable(t)}
                    style={{
                      position: 'absolute',
                      left: Number(t.monitor_x || 0) * f, top: Number(t.monitor_y || 0) * f,
                      width: w, height: h, borderRadius: 9, cursor: 'pointer',
                      border: `2px solid ${open ? '#198754' : seated ? '#0d6efd' : '#999'}`,
                      background: open ? '#198754' : seated ? 'rgba(13,110,253,0.12)' : 'rgba(255,255,255,0.92)',
                      color: open ? 'white' : seated ? '#0a58ca' : '#222', fontWeight: 700,
                      fontSize: Math.max(activeArea === '__all__' ? 9 : 11, Math.min(15, 13 * f)), padding: 0,
                    }}>
                    {t.number}
                    {open && <div style={{ fontSize: 9, fontWeight: 500 }}>{eur(open.total_cents)}</div>}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Υπόμνημα: τι σημαίνει το χρώμα κάθε τραπεζιού — όλα δέχονται παραγγελία */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, opacity: 0.75, margin: '6px 2px 0' }}>
            <span><span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: '#198754', verticalAlign: -1 }} /> {tr('Open order')}</span>
            <span><span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: 'rgba(13,110,253,0.25)', border: '1.5px solid #0d6efd', verticalAlign: -1 }} /> {tr('Seated (sitting)')}</span>
            <span><span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: '#fff', border: '1.5px solid #999', verticalAlign: -1 }} /> {tr('No sitting yet — tapping creates one')}</span>
          </div>
          {/* Χειροκίνητο zoom πάνω στο auto-fit του χώρου */}
          <div style={{ position: 'absolute', right: 10, top: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              ['➕', tr('Zoom in'), () => setZoom((z) => Math.min(4, +(z * 1.3).toFixed(2)))],
              ['➖', tr('Zoom out'), () => setZoom((z) => Math.max(0.4, +(z / 1.3).toFixed(2)))],
              ['⤢', tr('Fit to area'), () => setZoom(1)],
            ].map(([icon, title, fn]) => (
              <button key={title} title={title} onClick={fn} style={{
                width: 36, height: 36, borderRadius: 10, border: '1px solid #ccc',
                background: 'rgba(255,255,255,0.95)', cursor: 'pointer', fontSize: 15,
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }}>{icon}</button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // ORDER VIEW: sticky header + Menu / Bill tabs + sticky cart bar
  // ============================================================
  const activeItems = menu.items.filter((i) => i.category === selCat && bit(i.active));
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 10px 120px' }}>
      {/* sticky header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 20, background: 'white',
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
        borderBottom: '1px solid #eee',
      }}>
        <button className="rlToolbarBtn" style={{ padding: '7px 12px' }} onClick={backToMap}>←</button>
        <div style={{ lineHeight: 1.15 }}>
          <b style={{ fontSize: 16 }}>{tr('Table')} {(order?.tables || []).map((t) => t.number).join('+')}</b>
          {order?.order?.reservation ? (
            <div style={{ fontSize: 11, opacity: 0.6 }}>{tr('Reservation')} #{order.order.reservation}</div>
          ) : null}
        </div>
        <b style={{ marginLeft: 'auto', fontSize: 17, color: balance > 0 ? '#b02a37' : '#198754' }}>
          {eur(totals ? totals.total : 0)}
        </b>
        <div style={{ position: 'relative' }}>
          <button className="rlToolbarBtn" style={{ padding: '7px 12px' }} onClick={() => setMoreOpen((v) => !v)}>⋮</button>
          {moreOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '110%', background: 'white', zIndex: 30,
              border: '1px solid #ccc', borderRadius: 10, boxShadow: '0 6px 22px rgba(0,0,0,0.15)',
              display: 'flex', flexDirection: 'column', minWidth: 210,
            }}>
              {[
                ['\u{1F3AB} ' + tr('Guest QR'), () => { setMoreOpen(false); mintGuestLink(); }],
                ['🧾 ' + tr('Receipt'), () => { setMoreOpen(false); issueReceipt(); }],
                ['🗑 ' + tr('Void order'), () => { setMoreOpen(false); voidOrder(); }],
              ].map(([label, fn], i) => (
                <button key={i} onClick={fn} style={{
                  border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer',
                  padding: '12px 14px', fontSize: 14, borderBottom: '1px solid #f2f2f2',
                }}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>


      {/* tabs */}
      <div style={{ display: 'flex', gap: 8, margin: '10px 0' }}>
        <button style={{ ...chipStyle(tab === 'menu'), flex: 1, textAlign: 'center' }}
          onClick={() => setTab('menu')}>{tr('Menu')}</button>
        <button style={{ ...chipStyle(tab === 'bill'), flex: 1, textAlign: 'center' }}
          onClick={() => setTab('bill')}>
          {tr('Bill')} · {eur(totals ? totals.total : 0)}
        </button>
      </div>

      {tab === 'menu' && (
        <>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
            {(menu.courses || []).map((crs) => (
              <button key={crs.id} style={chipStyle(course === crs.id)}
                onClick={() => {
                  setCourse(crs.id);
                  const first = menu.categories.find((x) => bit(x.active) && Number(x.default_course || 2) === crs.id);
                  setSelCat(first ? first.id : null);
                }}>{crs.name}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '6px 0' }}>
            {menu.categories
              .filter((c) => bit(c.active) && Number(c.default_course || 2) === course)
              .map((c) => (
                <button key={c.id}
                  style={{ ...chipStyle(selCat === c.id), background: selCat === c.id ? '#333' : 'white', borderColor: selCat === c.id ? '#333' : '#ccc' }}
                  onClick={() => setSelCat(c.id)}>{c.name}</button>
              ))}
          </div>

          {/* items: full-width tap rows */}
          {activeItems.map((mi) => {
            const itemLines = cart.map((l, ix) => ({ l, ix })).filter(({ l }) => l.menu_item === mi.id);
            const showInline = expandedItems[mi.id] && itemLines.length > 0;
            const kgSum = itemLines.reduce((s, { l }) => s + Number(l.qty || 0), 0);
            return (
            <React.Fragment key={mi.id}>
            <button onClick={() => addToCart(mi)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                border: '1px solid #e3e3e3', borderRadius: 10, background: bit(mi.out_of_stock) ? '#f5f5f5' : 'white',
                opacity: bit(mi.out_of_stock) ? 0.5 : 1, cursor: 'pointer',
                padding: '13px 12px', marginBottom: 6, textAlign: 'left',
              }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{mi.name}</div>
                {mi.description && <div style={{ fontSize: 12, opacity: 0.6 }}>{mi.description}</div>}
                {bit(mi.is_meat) && mi.meat && meatDefaults.get(mi.meat)?.name ? (
                  <div style={{ fontSize: 11, opacity: 0.55 }}>{bbqIcon()} BBQ: <b>{meatDefaults.get(mi.meat).name}</b></div>
                ) : null}
              </div>
              {(() => {
                const sent = sentByItem.get(mi.id);
                const inCart = cartByItem.get(mi.id);
                const pre = bit(mi.is_meat) && mi.meat ? preByMeat.get(mi.meat) : null;
                const badge = (bg, fg, text) => (
                  <span style={{ background: bg, color: fg, borderRadius: 10, padding: '2px 8px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>
                );
                const cartLines = cart.filter((l) => l.menu_item === mi.id);
                const cartText = bit(mi.per_kg) ? `${cartLines.length}` : `${inCart}×`;
                return (
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    {pre ? badge('rgba(253,126,20,0.15)', '#a35510', `🍖 ${pre.grams}${tr('g')}`) : null}
                    {sent ? badge('rgba(25,135,84,0.14)', '#146c43', `✓ ${sent}${bit(mi.per_kg) ? tr('g') : '×'}`) : null}
                    {inCart ? (
                      // Μεγάλο, ευκολοπάτητο: ανοίγει τις λεπτομέρειες γραμμής (ποσότητα/αφαίρεση)
                      <span onClick={(e) => { e.stopPropagation(); toggleExpand(mi.id); }}
                        style={{
                          background: 'rgba(13,110,253,0.13)', color: '#0a58ca', borderRadius: 14,
                          padding: '8px 14px', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
                          border: '1.5px solid rgba(13,110,253,0.35)', display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>🛒 {cartText}</span>
                    ) : null}
                  </span>
                );
              })()}
              <span style={{ fontSize: 12, whiteSpace: 'nowrap', opacity: 0.5 }}>
                {eur(mi.price_cents)}{bit(mi.per_kg) ? '/kg' : ''}
              </span>
              <span style={{
                width: 30, height: 30, borderRadius: 15, background: '#198754', color: 'white',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 19,
              }}>+</span>
            </button>
            {showInline && (
              <div style={{
                margin: '-4px 0 8px', padding: '4px 10px 8px',
                border: '1px dashed #b9d8c6', borderTop: 'none', borderRadius: '0 0 10px 10px',
                background: 'rgba(25,135,84,0.04)',
              }}>
                {itemLines.map(({ l, ix }) => renderCartLine(l, ix, false))}
                {bit(mi.per_kg) && (
                  <div style={{ fontSize: 13, textAlign: 'right', marginTop: 4 }}>
                    {itemLines.length > 1 && <span style={{ opacity: 0.6 }}>{itemLines.map(({ l }) => l.qty).join(' + ')} = </span>}
                    <b>{kgSum}{tr('g')}</b> · {eur(Math.round(kgSum * mi.price_cents / 1000))}
                  </div>
                )}
              </div>
            )}
            </React.Fragment>
            );
          })}
        </>
      )}

      {tab === 'bill' && (
        <>
          {draftCount > 0 && (
            <button className="rlToolbarBtn rlToolbarBtnPrimary" style={{ width: '100%', marginBottom: 10, padding: 12 }}
              onClick={approveDrafts}>
              {tr('Approve guest items')} ({draftCount})
            </button>
          )}

          {sentItems.length === 0 && <p style={{ opacity: 0.6 }}>{tr('Nothing sent yet.')}</p>}
          {sentItems.map((i) => (
            <div key={i.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 4px',
              borderBottom: '1px dashed #e5e5e5', fontSize: 14,
            }}>
              <span style={{ flex: 1 }}>
                {i.qty}{bit(i.per_kg) ? tr('g') : '×'} {i.item_name}
                {i.note ? <i style={{ opacity: 0.6 }}> — {i.note}</i> : null}
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: i.status === 'served' ? '#198754' : i.status === 'ready' ? '#d6a400' : '#888',
                }}>{tr(i.status)}</div>
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>{eur(lineTotal({ qty: i.qty }, i.unit_price_cents, bit(i.per_kg)))}</span>
              <button title={tr('Treat (on the house)')} onClick={() => addDiscount('treat', 0, i.id)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 17, padding: 4 }}>🎁</button>
              <button title={tr('Void item')} onClick={() => voidItem(i)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#b02a37', fontSize: 15, padding: 4 }}>✕</button>
            </div>
          ))}

          {totals && (
            <div style={{ marginTop: 14, fontSize: 15 }}>
              <div style={{ display: 'flex', padding: '3px 0' }}>
                <span style={{ flex: 1 }}>{tr('Subtotal')}</span><b>{eur(totals.gross)}</b>
              </div>
              {totals.discount > 0 && (
                <div style={{ display: 'flex', padding: '3px 0', color: '#b02a37' }}>
                  <span style={{ flex: 1 }}>{tr('Discounts')}</span><b>-{eur(totals.discount)}</b>
                </div>
              )}
              <div style={{ display: 'flex', padding: '3px 0' }}>
                <span style={{ flex: 1 }}>{tr('Paid')}</span><b>{eur(totals.paid)}</b>
              </div>
              <div style={{ display: 'flex', padding: '6px 0', fontSize: 19, borderTop: '2px solid #333' }}>
                <span style={{ flex: 1 }}>{tr('Balance')}</span>
                <b style={{ color: balance > 0 ? '#b02a37' : '#198754' }}>{eur(balance)}</b>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input placeholder={tr('Discount % or €')} value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #ccc' }} />
                <button className="rlToolbarBtn" onClick={() => {
                  const v = String(discount).trim();
                  if (!v) return;
                  if (v.endsWith('%')) addDiscount('percent', parseFloat(v) || 0);
                  else addDiscount('amount', Math.round(parseFloat(v.replace(',', '.')) * 100) || 0);
                }}>{tr('Apply')}</button>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input placeholder={eur(balance)} value={cashAmount} inputMode="decimal"
                  onChange={(e) => setCashAmount(e.target.value)}
                  style={{ width: 110, padding: 10, borderRadius: 8, border: '1px solid #ccc' }} />
                <button className="rlToolbarBtn rlToolbarBtnPrimary" style={{ flex: 1, padding: 12 }}
                  disabled={balance <= 0} onClick={payCash}>💶 {tr('Cash')}</button>
                <button className="rlToolbarBtn" style={{ flex: 1 }}
                  disabled={balance <= 0} onClick={payTerminal}>💳 {tr('Card')}</button>
              </div>

              {(order?.payments || []).map((p) => (
                <div key={p.id} style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                  {p.method === 'cash' ? '💶' : '💳'} {eur(p.amount_cents)} — {tr(p.status)}
                </div>
              ))}

              <button className="rlToolbarBtn" style={{ width: '100%', marginTop: 14, padding: 12 }}
                onClick={closeOrder}>
                ✔ {tr('Close order')}
              </button>
            </div>
          )}
        </>
      )}

      {/* sticky cart bar */}
      {cart.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, zIndex: 9998,
          bottom: isMobile ? 64 : 0,
          background: 'white', borderTop: '2px solid #198754',
          boxShadow: '0 -4px 18px rgba(0,0,0,0.12)',
          padding: `8px 12px calc(8px + env(safe-area-inset-bottom, 0px))`,
        }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            {cartOpen && (
              <div style={{ maxHeight: '38vh', overflowY: 'auto', marginBottom: 8 }}>
                {cart.map((l, ix) => renderCartLine(l, ix))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="rlToolbarBtn" style={{ flex: 1, padding: 12 }}
                onClick={() => setCartOpen((v) => !v)}>
                🛒 {cartCount} · {eur(cartTotal)} {cartOpen ? '▾' : '▴'}
              </button>
              <button className="rlToolbarBtn rlToolbarBtnPrimary" style={{ flex: 2, padding: 12, fontSize: 15 }}
                disabled={busy} onClick={sendCart}>
                {busy ? tr('Sending...') : tr('Send order ({{n}})', { n: cartCount })}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrModal && (
        <div onClick={() => setQrModal(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'white', borderRadius: 16, padding: 22, textAlign: 'center', maxWidth: 340,
          }}>
            <b style={{ display: 'block', marginBottom: 12 }}>{qrModal.title}</b>
            <QRCodeSVG value={qrModal.url} size={250} />
            <p style={{ fontSize: 11, wordBreak: 'break-all', opacity: 0.6, marginTop: 10 }}>{qrModal.url}</p>
            <button className="rlToolbarBtn" style={{ width: '100%', padding: 10, marginTop: 6 }}
              onClick={() => setQrModal(null)}>{tr('Close')}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
